/**
 * PromoterActionsDropdown.tsx - Promoter Management Actions Dropdown
 *
 * This component provides a dropdown menu with various management actions
 * for individual promoters including editing, analytics, and removal.
 *
 * Key Features:
 * - Comprehensive promoter action menu
 * - Click-outside detection for dropdown closing
 * - Icon-based actions with clear labeling
 * - Conditional action availability based on promoter state
 * - Responsive dropdown positioning
 * - Portal rendering for proper z-index layering
 *
 * Available Actions:
 * - Dashboard: View promoter performance dashboard
 * - Edit: Modify promoter information and settings
 * - Donate: Navigate to promoter-specific fundraiser page
 * - Invite: Send promoter invitation or reminder
 * - Link: Manage promoter-specific links
 * - Share: Social sharing for promoter context
 * - Remove: Remove promoter from fundraiser
 *
 * Props:
 * - onDashboard: () => void - Dashboard view handler
 * - onEdit: () => void - Edit promoter handler
 * - onDonate: () => void - Donate action handler
 * - onInvite: () => void - Invite action handler
 * - onLink: () => void - Link management handler
 * - onShare: () => void - Share action handler
 * - onRemove: () => void - Remove promoter handler
 *
 * Dependencies: Styled-components, Lucide icons, portal rendering
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import {
  ChevronDown,
  PieChart,
  Settings,
  Link,
  Share2,
  Trash2,
  Heart,
  UserPlus,
  Pen,
} from "lucide-react";

const DropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

const DropdownButtonContainer = styled.div`
  display: flex;
  align-items: center;
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  transition: all 0.2s ease;
`;

const EditMainButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 22px;
  background: transparent;
  border: none;
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  border-radius: 8px 0 0 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #1e293b;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DropdownTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  background: transparent;
  border: none;
  border-left: 1px solid #e2e8f0;
  color: #64748b;
  cursor: pointer;
  border-radius: 0 8px 8px 0;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 0, 0, 0.05);
    color: #1e293b;
  }
`;

const ChevronIcon = styled(ChevronDown)<{ isOpen: boolean }>`
  transition: transform 0.2s ease;
  transform: ${(props) => (props.isOpen ? "rotate(180deg)" : "rotate(0deg)")};
  flex-shrink: 0;
`;

const DropdownMenu = styled.div<{ isOpen: boolean; showAbove: boolean }>`
  position: absolute;
  top: ${(props) => (props.showAbove ? "auto" : "100%")};
  bottom: ${(props) => (props.showAbove ? "100%" : "auto")};
  right: 0;
  z-index: 1000;
  min-width: 200px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  visibility: ${(props) => (props.isOpen ? "visible" : "hidden")};
  transform: ${(props) =>
    props.isOpen
      ? props.showAbove
        ? "translateY(-4px)"
        : "translateY(4px)"
      : props.showAbove
        ? "translateY(-8px)"
        : "translateY(0)"};
  transition: all 0.2s ease;
  margin-top: ${(props) => (props.showAbove ? "0" : "4px")};
  margin-bottom: ${(props) => (props.showAbove ? "4px" : "0")};
`;

const DropdownItem = styled.button<{ variant?: "danger" }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: #ffffff;
  color: ${(props) => (props.variant === "danger" ? "#dc2626" : "#1e293b")};
  font-size: 14px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.2s ease;
  display: flex;
  align-items: center;
  gap: 12px;

  &:hover {
    background: ${(props) =>
      props.variant === "danger" ? "#fef2f2" : "#f8fafc"};
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f1f5f9;
  }
`;

const ItemIcon = styled.div<{ variant?: "danger" }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${(props) => (props.variant === "danger" ? "#dc2626" : "#64748b")};
  flex-shrink: 0;
`;

const Divider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 4px 0;
`;

interface PromoterActionsDropdownProps {
  onDashboard: () => void;
  onDonate: () => void;
  onInvite: () => void;
  onEdit: () => void;
  onCopyLink: () => void;
  onShare: () => void;
  onDelete: () => void;
  isNewPromoter?: boolean;
}

export const PromoterActionsDropdown: React.FC<
  PromoterActionsDropdownProps
> = ({
  onDashboard,
  onDonate,
  onInvite,
  onEdit,
  onCopyLink,
  onShare,
  onDelete,
  isNewPromoter,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAbove, setShowAbove] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleEditClick = () => {
    !isNewPromoter && onDashboard();
  };

  const handleDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    // Calculate if dropdown should show above based on available space
    const shouldShowAbove = calculateDropdownPosition();
    setShowAbove(shouldShowAbove);

    setIsOpen(!isOpen);
  };

  const calculateDropdownPosition = (): boolean => {
    if (!dropdownRef.current) return false;

    const rect = dropdownRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownHeight = 200; // Approximate dropdown height
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Show above if there's not enough space below but enough space above
    return spaceBelow < dropdownHeight && spaceAbove > dropdownHeight;
  };

  const handleOptionClick = (onClick: () => void) => {
    onClick();
    setIsOpen(false);
  };

  return (
    <DropdownContainer ref={dropdownRef}>
      <DropdownButtonContainer>
        <EditMainButton
          disabled={isNewPromoter}
          onClick={handleEditClick}
          type="button"
          className={isNewPromoter ? "disabled" : ""}
        >
          <PieChart size={16} />
          Dashboard
        </EditMainButton>
        <DropdownTrigger onClick={handleDropdownToggle} type="button">
          <ChevronIcon size={16} isOpen={isOpen} />
        </DropdownTrigger>
      </DropdownButtonContainer>

      <DropdownMenu isOpen={isOpen} showAbove={showAbove}>
        {!isNewPromoter && (
          <DropdownItem
            type="button"
            onClick={() => handleOptionClick(onDonate)}
          >
            <ItemIcon>
              <Heart size={16} />
            </ItemIcon>
            Donate
          </DropdownItem>
        )}
        {!isNewPromoter && (
          <DropdownItem
            type="button"
            onClick={() => handleOptionClick(onInvite)}
          >
            <ItemIcon>
              <UserPlus size={16} />
            </ItemIcon>
            Invite
          </DropdownItem>
        )}
        {!isNewPromoter && (
          <DropdownItem type="button" onClick={() => handleOptionClick(onEdit)}>
            <ItemIcon>
              <Pen size={16} />
            </ItemIcon>
            Edit
          </DropdownItem>
        )}
        {/* {!isNewPromoter && <Divider />} */}
        <DropdownItem
          variant="danger"
          type="button"
          onClick={() => handleOptionClick(onDelete)}
        >
          <ItemIcon variant="danger">
            <Trash2 size={16} />
          </ItemIcon>
          Remove
        </DropdownItem>
      </DropdownMenu>
    </DropdownContainer>
  );
};
